import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UsersThree, Plus, Copy, ArrowRight } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function CoachDashboard() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [code, setCode] = useState(user?.coach_invite_code || null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/coach/players");
      setPlayers(data);
    } catch (e) {
      // subscription needed
    }
  };
  useEffect(() => { load(); }, []);

  const genCode = async () => {
    try {
      const { data } = await api.post("/coach/invite-code");
      setCode(data.code);
      toast.success("New code generated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openPlayer = async (p) => {
    setSelected(p);
    const { data } = await api.get(`/coach/player/${p._id}`);
    setDetail(data);
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    try {
      await api.post(`/coach/player/${selected._id}/note`, { content: note });
      setNote("");
      const { data } = await api.get(`/coach/player/${selected._id}`);
      setDetail(data);
      toast.success("Note sent");
    } catch { toast.error("Failed"); }
  };

  const isSubscribed = user?.subscription_tier === "coach_pro";

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Coach Ops</div>
            <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
              <UsersThree size={48} weight="duotone" className="text-[#FF3B30]" /> Your Squad
            </h1>
          </div>
          {isSubscribed && (
            <div className="flex items-center gap-3">
              {code && (
                <div className="border border-[#FF3B30] px-4 py-2 flex items-center gap-2 bg-[#FF3B30]/10" data-testid="invite-code">
                  <span className="text-xs uppercase tracking-widest text-[#A3A3A3]">Code</span>
                  <span className="font-heading font-black text-xl">{code}</span>
                  <button onClick={() => { navigator.clipboard.writeText(code); toast.success("Copied"); }} data-testid="copy-code">
                    <Copy size={16} className="text-[#FF3B30]" />
                  </button>
                </div>
              )}
              <Button onClick={genCode} data-testid="generate-code"
                className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11 px-6">
                <Plus size={18} weight="bold" className="mr-2" /> {code ? "New Code" : "Generate Code"}
              </Button>
            </div>
          )}
        </div>

        {!isSubscribed && (
          <div className="border border-[#FF3B30] bg-[#FF3B30]/10 p-8 text-center" data-testid="coach-upgrade">
            <div className="font-heading font-black uppercase text-2xl mb-2">Coach Subscription Required</div>
            <p className="text-[#A3A3A3] mb-6">Unlock the Coach Pro plan to manage your players.</p>
            <Link to="/pricing" className="inline-block bg-[#FF3B30] hover:bg-[#D63026] text-white px-6 py-3 font-heading uppercase tracking-widest text-sm font-bold">
              See Coach Plan
            </Link>
          </div>
        )}

        {isSubscribed && (
          <>
            {players.length === 0 && (
              <div className="border-2 border-dashed border-[#262626] p-16 text-center" data-testid="no-players">
                <UsersThree size={48} className="text-[#A3A3A3] mx-auto mb-4" />
                <div className="font-heading uppercase tracking-widest text-[#A3A3A3]">No players linked yet</div>
                <div className="text-sm text-[#A3A3A3] mt-2">Share your invite code with Elite-tier players.</div>
              </div>
            )}
            {players.length > 0 && (
              <div className="border border-[#262626] bg-[#121212]">
                <div className="grid grid-cols-12 gap-4 p-4 border-b border-[#262626] text-xs font-heading uppercase tracking-widest text-[#A3A3A3]">
                  <div className="col-span-3">Player</div>
                  <div className="col-span-2">Position</div>
                  <div className="col-span-3">Current → Aspiring</div>
                  <div className="col-span-2">Tier</div>
                  <div className="col-span-2 text-right">View</div>
                </div>
                {players.map((p) => (
                  <button key={p._id} onClick={() => openPlayer(p)} data-testid={`player-${p._id}`}
                    className="w-full grid grid-cols-12 gap-4 p-4 border-b border-[#1C1C1C] last:border-0 text-sm hover:bg-[#1C1C1C] transition-colors text-left">
                    <div className="col-span-3 font-semibold">{p.name}</div>
                    <div className="col-span-2 text-[#A3A3A3]">{p.profile?.position || "—"}</div>
                    <div className="col-span-3 text-[#A3A3A3] text-xs">
                      {p.profile ? `${p.profile.current_level.replace("_", " ")} → ${p.profile.aspirational_level.replace("_", " ")}` : "No profile"}
                    </div>
                    <div className="col-span-2">
                      <span className="border border-[#FF3B30] text-[#FF3B30] px-2 py-0.5 text-xs font-heading uppercase tracking-widest">
                        {p.subscription_tier?.toUpperCase()}
                      </span>
                    </div>
                    <div className="col-span-2 text-right"><ArrowRight size={16} className="inline text-[#A3A3A3]" /></div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setDetail(null))}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-none max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase text-2xl">{selected?.name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-xs uppercase tracking-widest text-[#A3A3A3]">Position</span><div className="font-heading font-bold">{detail.player.profile?.position}</div></div>
                <div><span className="text-xs uppercase tracking-widest text-[#A3A3A3]">Level</span><div className="font-heading font-bold">{detail.player.profile?.current_level}</div></div>
                <div><span className="text-xs uppercase tracking-widest text-[#A3A3A3]">Aspiring</span><div className="font-heading font-bold">{detail.player.profile?.aspirational_level}</div></div>
              </div>
              <div className="border border-[#262626] p-4">
                <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3] mb-2">Active Goals ({detail.goals.length})</div>
                {detail.goals.slice(0, 5).map((g) => (
                  <div key={g.id} className="flex justify-between text-sm py-1">
                    <span>{g.title}</span>
                    <span className="text-[#FF3B30] font-heading">{g.current_value}/{g.target_value} {g.unit}</span>
                  </div>
                ))}
              </div>
              <div className="border border-[#262626] p-4">
                <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3] mb-2">Recent Progress</div>
                {detail.progress.slice(0, 5).map((l) => (
                  <div key={l.id} className="flex justify-between text-sm py-1">
                    <span>{l.metric}</span>
                    <span className="font-heading">{l.value} {l.unit}</span>
                  </div>
                ))}
              </div>
              {detail.matches && detail.matches.length > 0 && (
                <div className="border border-[#262626] p-4">
                  <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3] mb-2">Recent Matches</div>
                  {detail.matches.slice(0, 5).map((m) => (
                    <div key={m.id} className="py-2 border-b border-[#1C1C1C] last:border-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className={`font-heading font-black w-6 h-6 flex items-center justify-center text-xs ${
                          m.result === "W" ? "bg-[#34C759] text-white" : m.result === "L" ? "bg-[#FF3B30] text-white" : "bg-[#FF9F0A] text-black"
                        }`}>{m.result}</span>
                        <span className="font-semibold">vs {m.opponent}</span>
                        <span className="text-xs text-[#A3A3A3]">{m.match_date}</span>
                        {m.self_rating && <span className="text-xs text-[#A3A3A3]">· {m.self_rating}/10</span>}
                      </div>
                      {(m.clips || []).length > 0 && (
                        <div className="mt-2 space-y-1 pl-8">
                          {m.clips.map((c) => (
                            <a key={c.id} href={c.url} target="_blank" rel="noreferrer" className="text-xs text-[#FF3B30] hover:underline block" data-testid={`coach-clip-${c.id}`}>
                              ▸ {c.title || "Watch clip"} {c.moment_note && <span className="text-[#A3A3A3]">— {c.moment_note}</span>}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="border border-[#262626] p-4">
                <Label className="font-heading uppercase tracking-widest text-xs">Send Note</Label>
                <div className="flex gap-2 mt-2">
                  <Input value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Guidance for this player…" data-testid="coach-note-input"
                    className="rounded-none bg-[#0A0A0A] border-[#262626]" />
                  <Button onClick={addNote} data-testid="coach-note-send"
                    className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold">Send</Button>
                </div>
                <div className="mt-4 space-y-2">
                  {detail.notes.slice(0, 5).map((n) => (
                    <div key={n.id} className="border-l-2 border-[#FF3B30] pl-3 py-1 text-sm">
                      <div className="text-xs text-[#A3A3A3]">{new Date(n.created_at).toLocaleDateString()}</div>
                      <div>{n.content}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
